import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixHubV1DataMovementSchedulerServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/data-move-scheduler',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/wix-data-hub-scheduler',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_data_data-sync-jobs';

/**
 * Creates a scheduled data workflow with a cron schedule.
 *
 * The scheduled data workflow automatically runs according to the specified cron expression.
 * Each execution creates a new [data workflow](https://dev.wix.com/docs/api-reference/business-solutions/cms/operations/data-movement-jobs/introduction) with its `scheduleId` set to this scheduled data workflow's ID.
 */
export function createScheduledDataWorkflow(
  payload: object
): RequestOptionsFactory<any> {
  function __createScheduledDataWorkflow({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'scheduledDataWorkflow.lastExecutionDate' },
          { path: 'scheduledDataWorkflow.nextExecutionDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'POST' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.CreateScheduledDataWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath: '/v1/scheduled-data-workflows',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'scheduledDataWorkflow.lastExecutionDate' },
              { path: 'scheduledDataWorkflow.nextExecutionDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createScheduledDataWorkflow;
}

/**
 * Reinitializes a scheduled data workflow based on the current cron expression.
 *
 * This is needed in case an [updated](https://dev.wix.com/docs/api-reference/business-solutions/cms/operations/data-sync-jobs/update-data-sync-job) scheduled data workflow gets out of sync.
 * For example, if you update the cron expression, it may not initially run according to the new expression.
 * This reschedules it to run according to the new expression.
 *
 * Only applies to `enabled` scheduled data workflows.
 */
export function rescheduleScheduledDataWorkflow(
  payload: object
): RequestOptionsFactory<any> {
  function __rescheduleScheduledDataWorkflow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'POST' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.RescheduleScheduledDataWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath:
          '/v1/scheduled-data-workflows/{scheduledDataWorkflowId}/reschedule',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'nextExecutionDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __rescheduleScheduledDataWorkflow;
}

/**
 * Enqueues an immediate execution for a scheduled data workflow.
 *
 * This custom action doesn't change the cron schedule.
 * It triggers the same data workflow execution path used by scheduled
 * TimeCapsule runs.
 */
export function triggerScheduledDataWorkflowNow(
  payload: object
): RequestOptionsFactory<any> {
  function __triggerScheduledDataWorkflowNow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'POST' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.TriggerScheduledDataWorkflowNow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath:
          '/v1/scheduled-data-workflows/{scheduledDataWorkflowId}/run-now',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __triggerScheduledDataWorkflowNow;
}

/** Updates an existing scheduled data workflow. */
export function updateScheduledDataWorkflow(
  payload: object
): RequestOptionsFactory<any> {
  function __updateScheduledDataWorkflow({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'scheduledDataWorkflow.lastExecutionDate' },
          { path: 'scheduledDataWorkflow.nextExecutionDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'PATCH' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.UpdateScheduledDataWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath: '/v1/scheduled-data-workflows',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'scheduledDataWorkflow.lastExecutionDate' },
              { path: 'scheduledDataWorkflow.nextExecutionDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateScheduledDataWorkflow;
}

/**
 * Deletes a scheduled data workflow.
 *
 * This permanently removes the schedule and prevents future executions. Any currently running data workflows triggered by the schedule continue to completion.
 */
export function deleteScheduledDataWorkflow(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteScheduledDataWorkflow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'DELETE' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.DeleteScheduledDataWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath: '/v1/scheduled-data-workflows/{scheduledDataWorkflowId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteScheduledDataWorkflow;
}

/** Retrieves a scheduled data workflow by ID. */
export function getScheduledDataWorkflow(
  payload: object
): RequestOptionsFactory<any> {
  function __getScheduledDataWorkflow({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'GET' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.GetScheduledDataWorkflow',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath: '/v1/scheduled-data-workflows/{scheduledDataWorkflowId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'scheduledDataWorkflow.lastExecutionDate' },
              { path: 'scheduledDataWorkflow.nextExecutionDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getScheduledDataWorkflow;
}

/**
 * Retrieves all scheduled data workflows for the current site.
 *
 * The current implementation returns both enabled and disabled schedules. Request `paging` and `filter` fields are accepted for forward compatibility but are not applied by the service.
 */
export function listScheduledDataWorkflows(
  payload: object
): RequestOptionsFactory<any> {
  function __listScheduledDataWorkflows({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.data.sync.v1.jobs',
      method: 'GET' as any,
      methodFqn:
        'wix.hub.v1.DataMovementSchedulerService.ListScheduledDataWorkflows',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementSchedulerServiceUrl({
        protoPath: '/v1/scheduled-data-workflows',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'scheduledDataWorkflows.lastExecutionDate' },
              { path: 'scheduledDataWorkflows.nextExecutionDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listScheduledDataWorkflows;
}
